import React, { useState, useEffect } from 'react';
import apiClient from '../../../api/client';

// =====================================================================
// Types
// =====================================================================

interface ELNEntry {
  entry_id: number;
  batch_id: number;
  batch_code: string;
  title: string;
  discipline: string;
  content: string | null;
  entry_type: string;
  created_at: string | null;
  updated_at: string | null;
}

interface ELNSectionProps {
  batchCode: string;
  smiles?: string;
  projectId?: number;
  latestAssay?: string;
}

// =====================================================================
// Helpers
// =====================================================================

const DISCIPLINES = ['Chemistry', 'Biology', 'Analytical', 'Formulation'];

const DISCIPLINE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  Chemistry:    { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
  Biology:      { bg: '#d1fae5', text: '#065f46', border: '#6ee7b7' },
  Analytical:   { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
  Formulation:  { bg: '#ede9fe', text: '#5b21b6', border: '#c4b5fd' },
};

function formatDate(iso: string | null): string {
  if (!iso) return '\u2014';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

// =====================================================================
// Component
// =====================================================================

const ELNSection: React.FC<ELNSectionProps> = ({ batchCode, smiles, projectId, latestAssay }) => {
  const [entries, setEntries] = useState<ELNEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Create form
  const [showForm, setShowForm] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formDiscipline, setFormDiscipline] = useState('Chemistry');
  const [formContent, setFormContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const resp = await apiClient.get(`/batches/eln/entries/${batchCode}`);
      setEntries(resp.data || []);
    } catch { setEntries([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchEntries(); }, [batchCode]);

  const handleCreate = async () => {
    if (!formTitle.trim()) return;
    setSubmitting(true);
    setFormError(null);
    try {
      await apiClient.post(`/batches/eln/entries/${batchCode}`, {
        title: formTitle.trim(),
        discipline: formDiscipline,
        content: formContent.trim() || null,
      });
      setShowForm(false);
      setFormTitle('');
      setFormContent('');
      setFormDiscipline('Chemistry');
      fetchEntries();
    } catch (err: any) {
      setFormError(err?.response?.data?.detail || 'Failed to create entry');
    } finally { setSubmitting(false); }
  };

  const openForm = () => {
    setFormTitle(`Experiment: ${batchCode}`);
    setFormContent('');
    setFormDiscipline('Chemistry');
    setFormError(null);
    setShowForm(true);
  };

  return (
    <div onClick={e => e.stopPropagation()} style={{ marginTop: 4 }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={13} height={13}>
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
          </svg>
          <span style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
            Lab Notebook Entries
          </span>
          {!loading && <span style={{ fontSize: 10, color: '#9ca3af' }}>({entries.length})</span>}
        </div>
        {!showForm && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button onClick={openForm}
              style={{
                fontSize: 11, color: '#0d9488', background: 'none',
                border: '1px solid #99f6e4', borderRadius: 4, padding: '2px 8px',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3,
              }}>
              <span style={{ fontSize: 13, lineHeight: 1 }}>+</span> Add Entry
            </button>
            <button
              onClick={() => {
                const params = new URLSearchParams();
                params.set('batch', batchCode);
                if (smiles) params.set('smiles', smiles);
                if (projectId) params.set('project', String(projectId));
                if (latestAssay) params.set('assay', latestAssay);
                window.open(`/workbench-api/eln/?${params.toString()}`, '_blank');
              }}
              style={{
                fontSize: 11, color: '#6b7280', background: 'none',
                border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px 8px',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3,
              }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width={11} height={11}>
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              Open in Lab Notebook
            </button>
          </div>
        )}
      </div>

      {/* Create form */}
      {showForm && (
        <div style={{ backgroundColor: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: 6, padding: 10, marginBottom: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>Title *</div>
              <input
                value={formTitle}
                onChange={e => setFormTitle(e.target.value)}
                placeholder="e.g. Synthesis of DJD-00001"
                style={{
                  width: '100%', padding: '5px 8px', fontSize: 12,
                  backgroundColor: '#ffffff', color: '#1e293b',
                  border: '1px solid #d1d5db', borderRadius: 4, outline: 'none',
                }}
                onFocus={e => { e.target.style.borderColor = '#0d9488'; }}
                onBlur={e => { e.target.style.borderColor = '#d1d5db'; }}
              />
            </div>
            <div>
              <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>Discipline</div>
              <select
                value={formDiscipline}
                onChange={e => setFormDiscipline(e.target.value)}
                style={{
                  padding: '5px 8px', fontSize: 12,
                  backgroundColor: '#ffffff', color: '#1e293b',
                  border: '1px solid #d1d5db', borderRadius: 4, outline: 'none',
                }}
              >
                {DISCIPLINES.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 10, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>Content</div>
            <textarea
              rows={4}
              value={formContent}
              onChange={e => setFormContent(e.target.value)}
              placeholder="Describe the experiment, procedure, observations..."
              style={{
                width: '100%', padding: '5px 8px', fontSize: 12, resize: 'vertical',
                backgroundColor: '#ffffff', color: '#1e293b',
                border: '1px solid #d1d5db', borderRadius: 4, outline: 'none',
                fontFamily: 'inherit',
              }}
              onFocus={e => { e.target.style.borderColor = '#0d9488'; }}
              onBlur={e => { e.target.style.borderColor = '#d1d5db'; }}
            />
          </div>
          {formError && <div style={{ fontSize: 12, color: '#991b1b', marginBottom: 6 }}>{formError}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleCreate} disabled={!formTitle.trim() || submitting}
              style={{
                padding: '5px 14px', fontSize: 12, borderRadius: 6, border: 'none',
                backgroundColor: '#0d9488', color: '#ffffff',
                cursor: formTitle.trim() && !submitting ? 'pointer' : 'not-allowed',
                opacity: formTitle.trim() && !submitting ? 1 : 0.5,
              }}>
              {submitting ? 'Saving...' : 'Save Entry'}
            </button>
            <button onClick={() => setShowForm(false)} disabled={submitting}
              style={{ padding: '5px 12px', fontSize: 12, borderRadius: 6, border: 'none', backgroundColor: 'transparent', color: '#6b7280', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && <div style={{ fontSize: 12, color: '#9ca3af', padding: '4px 0' }}>Loading entries...</div>}

      {/* Empty state */}
      {!loading && entries.length === 0 && !showForm && (
        <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic', padding: '2px 0' }}>No notebook entries</div>
      )}

      {/* Entries list */}
      {!loading && entries.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {entries.map(entry => {
            const isExpanded = expandedId === entry.entry_id;
            const dc = DISCIPLINE_COLORS[entry.discipline] || DISCIPLINE_COLORS.Chemistry;
            return (
              <div key={entry.entry_id}
                onClick={() => setExpandedId(isExpanded ? null : entry.entry_id)}
                style={{
                  backgroundColor: isExpanded ? '#fafafa' : '#ffffff',
                  border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px',
                  cursor: 'pointer', transition: 'background-color 0.15s',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                    <span style={{
                      display: 'inline-flex', padding: '1px 6px', borderRadius: 3,
                      fontSize: 10, fontWeight: 500,
                      backgroundColor: dc.bg, color: dc.text, border: `1px solid ${dc.border}`,
                      flexShrink: 0,
                    }}>
                      {entry.discipline}
                    </span>
                    <span style={{ fontSize: 12, color: '#111827', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {entry.title}
                    </span>
                  </div>
                  <span style={{ fontSize: 10, color: '#9ca3af', flexShrink: 0, marginLeft: 8 }}>
                    {formatDate(entry.created_at)}
                  </span>
                </div>
                {/* Preview line when collapsed */}
                {!isExpanded && entry.content && (
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {entry.content.split('\n')[0].slice(0, 120)}
                  </div>
                )}
                {/* Full content when expanded */}
                {isExpanded && entry.content && (
                  <div style={{ fontSize: 12, color: '#374151', marginTop: 6, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                    {entry.content}
                  </div>
                )}
                {isExpanded && !entry.content && (
                  <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic', marginTop: 6 }}>No content</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ELNSection;
